/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */
import React, { useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Autotext } from 'components/autotext'
import queryString from 'query-string'
import config from 'config'
import { LocaleContext } from '../../context/locale'
import { InfectionsChart } from 'components/infectionsChart'
import cantonNames from './cantonNames'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { Link as TALink, ScrollTracking } from '@ta-interaktiv/contentbricks'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { AutomaticArticleTeasers } from 'components/automaticArticleTeasers'
import { FetchErrorMessage } from 'components/fetchErrorMessage'
import Toggle from 'react-toggle'

import de from 'locales/de.yml'
import fr from 'locales/fr.yml'

const STRINGS = {
  de: de,
  fr: fr
}

const ARTICLE_ID = ['tdg', '24heures'].includes(parseHostname().publicationName)
  ? '300047209907'
  : '800154527486'
// FIXME: favicon is missing
/**
 * @this {any}
 */
export function AppComponent() {
  const isMobile = browserCheck.isMobile

  // parse query string
  const qs = queryString.parse(location.search)
  // infer canton from hostname
  let defaultCanton = 'ZH'
  let defaultLocale = 'de'
  const actualDomain = parseHostname().publicationName
  if (
    [
      'bernerzeitung',
      'derbund',
      'berneroberlaender',
      'langenthalertagblatt',
      'thunertagblatt'
    ].includes(actualDomain)
  ) {
    defaultCanton = 'BE'
  } else if (
    ['tagesanzeiger', 'zsz', 'zuonline', 'landbote'].includes(actualDomain)
  ) {
    defaultCanton = 'ZH'
  } else if (['bazonline'].includes(actualDomain)) {
    // TODO add randomization
    defaultCanton = 'BS'
  } else if (['24heures'].includes(actualDomain)) {
    defaultCanton = 'VD'
    defaultLocale = 'fr'
  } else if (['tdg'].includes(actualDomain)) {
    defaultCanton = 'GE'
    defaultLocale = 'fr'
  }
  const _kt = qs.kt ? qs.kt.toUpperCase() : defaultCanton
  const _locale = qs.lang ? qs.lang.toLowerCase() : defaultLocale

  // STATE declarations
  const [kt, setKt] = useState(_kt)
  const [locale, setLocale] = useState(_locale)
  const [showMeasures, setShowMeasures] = useState(false)
  const localization = STRINGS[locale]

  // FUNCTIONS
  /**
   * @param {any} kt
   */
  /**
   * @param {string} lang
   */
  const handleClick = (kt, lang) => {
    window.history.pushState(
      {
        kt: kt,
        lang: lang
      },
      '',
      '?kt=' + kt + '&lang=' + lang
    )
    setKt(kt)
    setLocale(lang)
  }

  const switchBasel = () => {
    window.dataLayer = window.dataLayer || []
    if (kt === 'BL') {
      window.dataLayer.push({
        event: 'Interactions',
        event_action: 'click',
        event_label: `switch:bs`
      })
      handleClick('BS', 'de')
    } else if (kt === 'BS') {
      window.dataLayer.push({
        event: 'Interactions',
        event_action: 'click',
        event_label: `switch:bl`
      })
      handleClick('BL', 'de')
    }
  }

  const Switch = ({ isOn, toggle, labels }) => {
    return (
      <div className='bigToggleWrapper'>
        <div className='center'>
          <div className={'label ' + (!isOn ? '' : 'off')}>{labels[0]}</div>
          <div className='toggleContainer'>
            <Toggle
              defaultChecked={isOn}
              icons={false}
              /**
               * @param {{ target: { checked: any; }; }} e
               */
              onChange={e => {
                toggle(e.target.checked)
              }}
            />
          </div>
          <div className={'label right ' + (isOn ? '' : 'off')}>
            {labels[1]}
          </div>
        </div>
      </div>
    )
  }
  // TODO read from config file
  const availableCantons = [
    {
      abbr: 'BE',
      lang: 'de'
    },
    {
      abbr: 'ZH',
      lang: 'de'
    },
    {
      abbr: 'BL',
      lang: 'de'
    },
    {
      abbr: 'BS',
      lang: 'de'
    },
    {
      abbr: 'VD',
      lang: 'fr'
    },
    {
      abbr: 'GE',
      lang: 'fr'
    }
  ]

  let urlToDashboard = ''
  if (locale === 'de') {
    urlToDashboard =
      'https://interaktiv.' +
      parseHostname().publicationDomain +
      '/2020/covid-19-ausbruch-im-vergleich/'
  } else if (locale === 'fr') {
    urlToDashboard =
      'https://interactif.' +
      parseHostname().publicationDomain +
      '/2020/covid19-aujourd-hui/'
  }
  // render
  return (
    <div className='index'>
      <LocaleContext.Provider value={locale}>
        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />
        <ScrollTracking />
        <header className='ui vertical segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1>
                {localization.title} {cantonNames[kt]}
              </h1>
              <h2>
                {localization.lead} {cantonNames[kt]}.
              </h2>
            </div>
          </div>
        </header>
        {availableCantons.map(canton => canton.abbr).includes(kt) && (
          <div className='ui vertical very fitted segment'>
            <div className='ui text container'>
              <div className='ta metainfo'>
                <div className='ta author'>{localization.authors}</div>
                <div className='ta updated'>{localization.publicationDate}</div>
              </div>
            </div>
            {/* Include Switch if canton is Basel */}
            {['BS', 'BL'].includes(kt) && (
              <div className='ui text container'>
                <Switch
                  isOn={kt === 'BL'}
                  toggle={switchBasel}
                  labels={['Basel-Stadt', 'Basel-Landschaft']}
                />
              </div>
            )}
            <div className='ui text container' style={{ fontStyle: 'italic' }}>
              {localization.endemic}
            </div>
          </div>
        )}

        <Footer
          questions={[
            localization.feedbackQuestionInformation,
            localization.feedbackQuestionDesign
          ]}
          articleId={ARTICLE_ID}
          mailTo='atm@tamedia.ch'
          project={`${localization.title} ${cantonNames[kt]} | ${
            parseHostname().publicationDomain
          }`}
          credits={[
            [
              localization.credits.concept.key,
              localization.credits.concept.value
            ],
            [
              localization.credits.sources.key,
              localization.credits.sources.value
            ]
          ]}
          hidePortfolioTeaser
        />
        {config.appEnv === 'development' && (
          <div className='ui text container debug'>
            <h3>DEBUG</h3>
            {availableCantons.map(canton => (
              <button
                key={canton.abbr}
                /* eslint-disable-next-line */
                onClick={handleClick.bind(this, canton['abbr'], canton['lang'])}
                className={kt === canton.abbr ? 'mockup selected' : 'mockup'}
              >
                {cantonNames[canton.abbr]}
              </button>
            ))}
          </div>
        )}
      </LocaleContext.Provider>
    </div>
  )
}
